import {
    Assessment,
    Business,
    Error as ErrorIcon,
    ShowChart,
    TrendingDown,
    TrendingUp,
    Warning,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import { WidgetProps } from '../types/index.js';

interface Impact {
  type: 'market' | 'sector' | 'stock';
  title: string;
  description: string;
  confidence: number;
  impact: 'high' | 'medium' | 'low';
  affectedStocks?: string[];
}

export const ImpactDashboardWidget: React.FC<WidgetProps> = ({
  widget,
}) => {
  // Get connected inputs
  const selectedImpact = widget.inputs.find(input => input.id === 'selectedImpact')?.value as Impact | undefined;
  const marketSentiment = widget.inputs.find(input => input.id === 'marketSentiment')?.value as string | undefined;
  const filteredImpacts = widget.inputs.find(input => input.id === 'filteredImpacts')?.value as Impact[] | undefined;

  // Calculate aggregate metrics
  const metrics = useMemo(() => {
    if (!filteredImpacts || filteredImpacts.length === 0) {
      return {
        totalImpacts: 0,
        highImpacts: 0,
        avgConfidence: 0,
        topSectors: [],
        mostAffectedStocks: [],
      };
    }

    const highImpacts = filteredImpacts.filter(i => i.impact === 'high').length;
    const avgConfidence = filteredImpacts.reduce((sum, i) => sum + (i.confidence || 0), 0) / filteredImpacts.length;
    
    // Count affected stocks
    const stockCounts = new Map<string, number>();
    filteredImpacts.forEach(impact => {
      impact.affectedStocks?.forEach(stock => {
        stockCounts.set(stock, (stockCounts.get(stock) || 0) + 1);
      });
    });
    
    const mostAffectedStocks = Array.from(stockCounts.entries())
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([stock, count]) => ({ stock, count }));

    return {
      totalImpacts: filteredImpacts.length,
      highImpacts,
      avgConfidence,
      topSectors: ['Technology', 'Finance', 'Healthcare'], // Could be calculated from real data
      mostAffectedStocks,
    };
  }, [filteredImpacts]);

  // Get icon for impact type
  const getImpactIcon = (type: string) => {
    switch (type) {
      case 'market':
        return <ShowChart />;
      case 'sector':
        return <Business />;
      case 'stock':
        return <Assessment />;
      default:
        return <ShowChart />;
    }
  };

  // Get sentiment icon
  const getSentimentIcon = (sentiment?: string) => {
    switch (sentiment) {
      case 'bullish':
        return <TrendingUp color="success" />;
      case 'bearish':
        return <TrendingDown color="error" />;
      default:
        return <Warning color="warning" />;
    }
  };

  // Get impact severity color
  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  if (!selectedImpact && !filteredImpacts) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Alert severity="info">
          Connect this widget to an Impact Analysis widget to see detailed impact insights
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Impact Dashboard</Typography>
          {marketSentiment && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getSentimentIcon(marketSentiment)}
              <Typography variant="body2" color="text.secondary">
                Market: {marketSentiment}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Metrics Overview */}
          {filteredImpacts && (
            <Grid item xs={12}>
      <Paper elevation={0} sx={{
        p: 2,
        borderRadius: 2,
        background: 'linear-gradient(180deg, rgba(20,20,20,0.6) 0%, rgba(10,10,10,0.6) 100%)',
        border: '1px solid rgba(255,255,255,0.08)',
        backdropFilter: 'blur(8px) saturate(140%)',
      }}>
                <Typography variant="subtitle2" gutterBottom>
                  Impact Overview
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4">{metrics.totalImpacts}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Impacts
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="error.main">
                        {metrics.highImpacts}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        High Severity
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4">
                        {(metrics.avgConfidence * 100).toFixed(0)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Avg Confidence
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4">
                        {metrics.mostAffectedStocks.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Stocks Affected
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Selected Impact Detail */}
          {selectedImpact && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                    {getImpactIcon(selectedImpact.type)}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {selectedImpact.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {selectedImpact.description}
                      </Typography>
                    </Box>
                    <Chip
                      label={selectedImpact.impact.toUpperCase()}
                      color={getImpactColor(selectedImpact.impact)}
                      size="small"
                    />
                  </Box>

                  {/* Confidence Bar */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Confidence Level</Typography>
                      <Typography variant="body2">
                        {(selectedImpact.confidence * 100).toFixed(0)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={selectedImpact.confidence * 100}
                      sx={{ height: 8, borderRadius: 4 }}
                      color={selectedImpact.confidence > 0.8 ? 'success' : 'warning'}
                    />
                  </Box>

                  {/* Affected Stocks */}
                  {selectedImpact.affectedStocks && selectedImpact.affectedStocks.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Affected Stocks
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {selectedImpact.affectedStocks.map((stock) => (
                          <Chip
                            key={stock}
                            label={stock}
                            size="small"
                            variant="outlined"
                            icon={selectedImpact.impact === 'high' ? <ErrorIcon /> : <Warning />}
                          />
                        ))}
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Most Affected Stocks */}
          {metrics.mostAffectedStocks.length > 0 && (
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Most Affected Stocks
                </Typography>
                <List dense>
                  {metrics.mostAffectedStocks.map(({ stock, count }) => (
                    <ListItem key={stock}>
                      <ListItemIcon>
                        <Assessment />
                      </ListItemIcon>
                      <ListItemText
                        primary={stock}
                        secondary={`${count} impact${count > 1 ? 's' : ''}`}
                      />
                      <Chip
                        label={count}
                        size="small"
                        color={count > 2 ? 'error' : 'warning'}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          )}

          {/* Impact Type Distribution */}
          {filteredImpacts && filteredImpacts.length > 0 && (
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Impact Distribution
                </Typography>
                <List dense>
                  {['market', 'sector', 'stock'].map((type) => {
                    const count = filteredImpacts.filter(i => i.type === type).length;
                    const percentage = (count / filteredImpacts.length) * 100;
                    return (
                      <ListItem key={type}>
                        <ListItemIcon>
                          {getImpactIcon(type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={type.charAt(0).toUpperCase() + type.slice(1)}
                          secondary={
                            <LinearProgress
                              variant="determinate"
                              value={percentage}
                              sx={{ mt: 1 }}
                            />
                          }
                        />
                        <Typography variant="body2">
                          {count}
                        </Typography>
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
};